/**
 * 試験例題ページ
 *************************************************/
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { API, Auth, graphqlOperation } from 'aws-amplify';
import { GraphQLResult } from '@aws-amplify/api';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Button,
  Container,
  Divider,
  Dropdown,
  Form,
  Grid,
  Header,
  Icon,
  Segment,
  Image,
  Message,
  Dimmer,
  Loader,
  DropdownProps,
} from 'semantic-ui-react';
import { categoryByNo, quizzesSortedByNo } from 'graphql/queries';
import Footer from 'components/common/Footer';
import TimeUpModal from 'components/common/TimeUpModal';
import { CategoryByNoQuery, QuizzesSortedByNoQuery } from 'API';
import ConfirmDialog from 'components/common/ConfirmDialog';
import { endLoading, startLoading } from 'modules/commonModule';

type LocationState = {
  userId: string;
  categoryNo: number;
};
const ExamplePage: FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation<LocationState>();
  const { userId, categoryNo } = location.state;
  const [time, setTime] = useState<number>(Number(sessionStorage.getItem('time')));
  const [intervalID, setIntervalID] = useState<number>();
  const [isOpenDialog, setIsOpenDialog] = useState<boolean>(false);
  const [isOpenTimeUpModal, setIsOpenTimeUpModal] = useState<boolean>(false);
  const [categoryState, setCategoryState] = useState<any>();
  const [practiceState, setPracticeState] = useState<any>();
  const [quizDto, setQuizDto] = useState<any>();
  const [condition, setCondition] = useState<any>({});
  const [correct, setCorrect] = useState<any>({});
  const [inCorrect, setInCorrect] = useState<any>({});
  const areaRef = useRef<HTMLDivElement>(null);

  // スクロール位置をTOPに
  useEffect(() => {
    if (areaRef.current) {
      window.scrollTo(0, areaRef.current.offsetTop);
    }
  }, []);

  /**
   * 文字列を全角から半角に変換
   */
  const toHanKaku = useCallback((str) => {
    return str.replace(/[Ａ-Ｚａ-ｚ０-９]/g, (s: string) => String.fromCharCode(s.charCodeAt(0) - 0xfee0));
  }, []);

  // 解答選択時の処理
  const handleChange = useCallback(
    (e, { name, value }) => {
      // 入力値の文字列を加工（半角スペース除去、小文字を大文字に、全角を半角に）
      const customValue = toHanKaku(value.trim().toUpperCase());
      // trim関数で文字列の空白を除去
      setCondition({ ...condition, [name]: customValue });
    },
    [condition, toHanKaku],
  );

  const handleSelectChange = useCallback(
    (e, { name, value }) => {
      setCondition({ ...condition, [name]: value });
    },
    [condition],
  );

  const handleSelectChange2 = useCallback(
    (event: React.SyntheticEvent<HTMLElement, Event>, data: DropdownProps) => {
      const { name, value, options } = data;
      const targetOption = options?.find((option) => option.value === value);

      if (targetOption) {
        const side = targetOption['data-side'];

        const inputAnswer = {
          leftAnswer: side === 'left' ? value : condition[name]?.leftAnswer || '',
          rightAnswer: side === 'right' ? value : condition[name]?.rightAnswer || '',
        };
        console.log(inputAnswer);
        setCondition({ ...condition, [name]: inputAnswer });
      }
    },
    [condition],
  );

  /**
   * 「解答を見る」ボタン押下時の処理
   */
  const onSubmit = useCallback(async () => {
    try {
      dispatch(startLoading());

      let correctMessage = {};
      let inCorrectMessage = {};
      Object.keys(condition).forEach((key) => {
        // 選択された問題番号に対応する回答番号を取得
        const matchNo = Number(key.slice(-1)) - 1;
        if (condition[key] === practiceState[matchNo].answer) {
          correctMessage = { ...correctMessage, [key]: `正解：答えは「${practiceState[matchNo].answer}」です` };
        } else {
          inCorrectMessage = { ...inCorrectMessage, [key]: `不正解：答えは「${practiceState[matchNo].answer}」です` };
        }
      });

      setCorrect({ ...correct, message: correctMessage });
      setInCorrect({ ...inCorrect, message: inCorrectMessage });
    } catch (e) {
      console.log(e);
    } finally {
      dispatch(endLoading());
    }
  }, [condition, correct, dispatch, inCorrect, practiceState]);

  /**
   * 試験問題ページへ遷移
   */
  const nextPage = useCallback(async () => {
    try {
      dispatch(startLoading());

      // ダイアログ or モーダルを閉じる
      setIsOpenDialog(false);
      setIsOpenTimeUpModal(false);

      if (categoryState) {
        // 問題の制限時間をセッションストレージに保持（次画面の初期値として利用）
        sessionStorage.setItem('time', String(categoryState.time_limit));

        // 試験問題へ遷移
        history.push('/question', {
          userId,
          categoryNo: categoryState.no,
          quizImagePath: categoryState.quiz_image_path,
          quizzes: quizDto,
          example: {
            description: categoryState.description,
            imagePath: categoryState.image_path ? categoryState.image_path : null,
          },
        });
      }
    } catch (e) {
      console.log(e);
    } finally {
      dispatch(endLoading());
    }
  }, [categoryState, dispatch, history, quizDto, userId]);

  // カウントダウン処理
  const countdown = useCallback(() => {
    setTime((prevTime: number) => {
      const remainingTime = prevTime - 1000;

      // セッションストレージに現時間を保持（画面リロード対策）
      sessionStorage.setItem('time', String(remainingTime));

      return remainingTime;
    });
  }, []);

  // 1秒ごとにカウントダウン処理を実行
  useEffect(() => {
    const id = window.setInterval(countdown, 1000);
    setIntervalID(id);
  }, [countdown]);

  // 制限時間切れ時に発火する処理
  useEffect(() => {
    if (time <= 0) {
      clearInterval(intervalID);
      setIsOpenTimeUpModal(true);
    }
  }, [intervalID, time]);

  // ブラウザバックを禁止
  useEffect(() => {
    history.push('/example', location.state);
  }, [history, location.state]);

  useEffect(() => {
    window.onpopstate = () => {
      return () => {
        window.onpopstate = null;
      };
    };
  }, []);

  //  ブラウザクローズ時の確認メッセージ;
  useEffect(() => {
    window.onbeforeunload = (e: BeforeUnloadEvent) => {
      e.preventDefault();

      return '';
    };
  }, []);

  useEffect(() => {
    (async () => {
      try {
        dispatch(startLoading());

        // 認証チェック
        const user = await Auth.currentAuthenticatedUser();

        // ユーザーステータスと表示中の問題番号が一致しなければ
        // 解答済の問題と判定してログイン画面へリダイレクト
        if (user.attributes['custom:status'] !== String(categoryNo) && user.attributes['custom:role'] !== 'tester') {
          history.push('/');
        }

        // 大問題を取得
        const resCategory = (await API.graphql(
          graphqlOperation(categoryByNo, { no: categoryNo }),
        )) as GraphQLResult<CategoryByNoQuery>;
        const category = resCategory.data?.categoryByNo?.items?.[0];

        // 小問題を取得
        const resQuiz = (await API.graphql(
          graphqlOperation(quizzesSortedByNo, {
            catergoryId: category?.id,
          }),
        )) as GraphQLResult<QuizzesSortedByNoQuery>;
        const allQuiz = resQuiz?.data?.quizzesSortedByNo?.items;

        // 練習問題を取得
        const practice = allQuiz?.filter((item) => item?.practice_flg === true);

        // 本番問題を取得
        const quizzes = allQuiz?.filter((item) => item?.practice_flg === false);

        if (category && quizzes && practice) {
          setCategoryState(category);
          setQuizDto(quizzes);
          setPracticeState(practice);
        }
      } catch (e) {
        console.log(e);
        // 認証できなかった場合はログイン画面へリダイレクト
        if (e === 'not authenticated') {
          history.push('/');
        }
      } finally {
        dispatch(endLoading());
      }
    })();
  }, [categoryNo, dispatch, history]);

  return (
    <>
      <div ref={areaRef} />
      {categoryState ? (
        <>
          <Container
            textAlign="center"
            fluid
            style={{
              background: 'black',
              position: 'sticky',
              top: 0,
              zIndex: 999,
            }}
          >
            <Header as="h1" size="large" style={{ padding: '15px 0', color: 'white' }} data-cy="time-limit">
              {`残り時間：${moment(time).format('mm分ss秒')}`}
            </Header>
          </Container>

          <Container
            textAlign="center"
            fluid
            style={{
              background: 'white',
              position: 'sticky',
              top: '60px',
              zIndex: 999,
              margin: 0,
              padding: '5px 0',
            }}
          >
            <Divider section horizontal>
              <Header as="h1" size="huge" data-cy="example-title">
                {`問題${categoryState?.no}のやり方`}
              </Header>
            </Divider>
          </Container>

          <Container style={{ marginTop: '5px', marginBottom: '70px' }}>
            <Form size="large" error={Object.keys(inCorrect).length > 0} success={Object.keys(correct).length > 0}>
              <Grid container>
                <Grid.Row>
                  <Grid.Column width={16}>
                    <Segment>
                      {categoryState?.image_path && (
                        <Image
                          src={`${process.env.PUBLIC_URL}/image${categoryState.image_path}`}
                          centered
                          size="large"
                        />
                      )}
                      <p
                        style={{ lineHeight: '1.7' }}
                        // 文字列に含まれる<br>タグを認識させるため、文字列をHTMLに変換
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{
                          __html: categoryState?.description,
                        }}
                      />
                    </Segment>
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={16}>
                    <Header as="h2" size="medium" dividing>
                      練習問題
                    </Header>
                  </Grid.Column>
                </Grid.Row>
                {practiceState?.map((quiz: any) => (
                  <>
                    <Grid.Column width={16} key={quiz.id}>
                      <Grid columns={2}>
                        <Grid.Column width={5}>
                          <p style={{ fontSize: '1.3rem', margin: '0' }}>
                            <span style={{ marginRight: '20px' }}>{`問${quiz.no}.`}</span>
                            <span style={{ fontFamily: 'MS Pゴシック, sans-serif' }}>{quiz.question}</span>
                          </p>
                        </Grid.Column>
                        <Grid.Column width={4}>
                          <Form.Input
                            name={`quizNo${quiz.no}`}
                            id={quiz.id}
                            size="mini"
                            maxLength="6"
                            onChange={handleChange}
                            data-cy="quiz-input"
                          />
                          <Message
                            error
                            size="mini"
                            content={inCorrect.message && inCorrect.message[`quizNo${quiz.no}`]}
                            data-cy="message-inCorrect"
                          />
                          <Message
                            success
                            size="mini"
                            content={correct.message && correct.message[`quizNo${quiz.no}`]}
                            data-cy="message-correct"
                          />
                        </Grid.Column>
                      </Grid>
                    </Grid.Column>
                  </>
                ))}

                <Grid.Row>
                  <Grid.Column width={16}>
                    <Button primary size="large" onClick={onSubmit} data-cy="answer-button">
                      解答を見る
                    </Button>
                  </Grid.Column>
                </Grid.Row>

                <Divider />

                <Grid.Row centered>
                  <Grid.Column width={6}>
                    <Button fluid primary size="huge" onClick={() => setIsOpenDialog(true)} data-cy="next-button">
                      問題へ進む
                      <Icon name="arrow right" />
                    </Button>
                    <ConfirmDialog
                      isOpen={isOpenDialog}
                      setIsOpen={setIsOpenDialog}
                      action={nextPage}
                      currentPage="example"
                      timeLimit={categoryState.time_limit}
                    />
                    <TimeUpModal isOpen={isOpenTimeUpModal} action={nextPage} currentPage="example" />
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Form>
          </Container>
        </>
      ) : (
        <>
          <Container style={{ paddingTop: '100px' }}>
            <Dimmer active inverted>
              <Loader size="large" data-cy="loading">
                Loading
              </Loader>
            </Dimmer>
          </Container>
        </>
      )}
      <Footer />
    </>
  );
};
export default ExamplePage;
